/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Anchor, CalendarIcon, EnvelopeClosedIcon, GlobeIcon, IdCardIcon, Link2Icon, LinkedInLogoIcon, MobileIcon, PersonIcon } from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import type { ClientRow } from '../../context/ClientManagementContext';
import {
  ContactRow,
  getContactLabel,
  getContactHref,
  isSocialContact,
  isConversationLink,
  extractSocialFromSyntheticEmail,
  getSocialProfileUrl,
  getSocialProfileLabel,
  formatBirthDate,
} from '../ClientProfilePanel/helpers';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';

export interface OverviewContactSectionProps {
  client: ClientRow;
}

const CONTACT_ICON_MAP: Record<string, ReactNode> = {
  email: <EnvelopeClosedIcon width={16} height={16} />,
  phone: <MobileIcon width={16} height={16} />,
  phone_home: <MobileIcon width={16} height={16} />,
  phone_work: <MobileIcon width={16} height={16} />,
  whatsapp: <WhatsAppIcon className="w-4 h-4" />,
  linkedin: <LinkedInLogoIcon width={16} height={16} />,
  facebook: <PersonIcon width={16} height={16} />,
  instagram: <PersonIcon width={16} height={16} />,
  website: <GlobeIcon width={16} height={16} />,
};

/**
 * Resolves the icon for a contact channel type.
 *
 * @param type - The contact channel type (email, phone, linkedin, ...).
 * @returns The matching channel icon, defaulting to a generic link icon.
 */
function getContactIcon(type: string) {
  return CONTACT_ICON_MAP[type] ?? <Link2Icon width={16} height={16} />;
}

/**
 * Contact SectionCard for client overview (channels, document, birth, address).
 *
 * @param props - The client row whose contact data is rendered.
 * @returns The rendered contact section card.
 */
export function OverviewContactSection({ client }: OverviewContactSectionProps) {
  const { t } = useTranslation('features/clients');
  const hasContacts = Array.isArray(client.contacts) && client.contacts.length > 0;
  const legacySocialInfo = !hasContacts
    ? extractSocialFromSyntheticEmail(client.email ?? '')
    : null;

  return (
    <SectionCard title={t('profile.overview.contactData')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-3)]">
        {hasContacts
          ? client.contacts!.map((contact, idx) => (
              <ContactChannelRow key={idx} contact={contact} t={t} />
            ))
          : legacySocialInfo
            ? (
              <ContactRow
                icon={<PersonIcon width={16} height={16} />}
                label={getSocialProfileLabel(legacySocialInfo.platform)}
                value={legacySocialInfo.platform === 'instagram'
                  // This legacy synthetic-email format predates the stored
                  // `handle`/username -- only the dead IGSID id survives, so
                  // there's no way to build a working link for it.
                  ? t('profile.overview.profileUnavailable')
                  : (
                    <Anchor
                      href={getSocialProfileUrl(
                        legacySocialInfo.platform,
                        legacySocialInfo.externalUserId,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-(--text-link) underline"
                    >
                      {t('profile.overview.viewProfile')}
                    </Anchor>
                  )}
              />
              )
            : (
              <>
                <ContactRow
                  icon={<EnvelopeClosedIcon width={16} height={16} />}
                  label={t('profile.overview.email')}
                  value={client.email || '—'}
                />
                <ContactRow
                  icon={<MobileIcon width={16} height={16} />}
                  label={t('profile.overview.phone')}
                  value={client.phone || '—'}
                />
              </>
              )}
        <ContactRow
          icon={<IdCardIcon width={16} height={16} />}
          label="CPF/CNPJ"
          value={client.cpf || client.cnpj || '—'}
        />
        <ContactRow
          icon={<CalendarIcon width={16} height={16} />}
          label={t('profile.overview.birthDate')}
          value={formatBirthDate(client.birth_date)}
        />
      </div>
    </SectionCard>
  );
}

interface ChannelProps {
  contact: { type: string; value: string; label?: string; handle?: string; source?: string };
  t: (key: string, options?: { defaultValue: string }) => string;
}

/**
 * Renders one contact channel as a linked or plain overview row.
 *
 * @param props - The contact channel and translation function.
 * @returns The rendered contact channel row.
 */
function ContactChannelRow({ contact, t }: ChannelProps) {
  const href = getContactHref(contact.type, contact.value, contact.handle, contact.source);
  // The drawer's contact-type names ("Telefone residencial"), so the card
  // reads in the same words the broker picked; unknown types keep the
  // humanized fallback.
  const label = contact.label
    || t(`drawer.contacts.types.${contact.type}`, { defaultValue: getContactLabel(contact.type) });
  const socialLabel = isConversationLink(contact.type, contact.source)
    ? t('profile.overview.openConversation')
    : t('profile.overview.viewProfile');
  const whatsAppHref = `https://wa.me/${contact.value.replace(/\D/g, "")}`;
  const whatsAppAction = contact.type === 'whatsapp' ? (
    <Anchor
      href={whatsAppHref}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-[var(--space-1)] px-[var(--space-2)] py-[var(--space-1)] rounded-(--radius-md) bg-[var(--status-success-subtle)] text-[var(--status-success)] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] hover:opacity-80 transition-opacity no-underline"
    >
      <WhatsAppIcon className="w-3.5 h-3.5" />
      {t('profile.overview.openWhatsApp')}
    </Anchor>
  ) : null;

  if (!href) {
    return (
      <ContactRow
        icon={getContactIcon(contact.type)}
        label={label}
        value={isSocialContact(contact.type) ? t('profile.overview.profileUnavailable') : contact.value}
        action={whatsAppAction}
      />
    );
  }
  return (
    <ContactRow
      icon={getContactIcon(contact.type)}
      label={label}
      value={(
        <Anchor
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-(--text-link) underline"
        >
          {isSocialContact(contact.type) ? socialLabel : contact.value}
        </Anchor>
      )}
      action={whatsAppAction}
    />
  );
}
